import React from 'react';
import { NextSeo, SoftwareAppJsonLd } from 'next-seo';
import { SEO } from '@/constants/seo';
import Layout from '@/layout/Layout';
import styled, { ThemeProvider } from 'styled-components';
import { darkTheme, lightTheme } from '@/constants/theme';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toolbar } from '@/containers/Toolbar';
import { Editor } from '@/containers/Editor';
import { BottomBar } from '@/containers/Editor/components/BottomBar';
import { useRouter } from 'next/router';
import { useMantineColorScheme } from '@mantine/core';
import useFile from '@/store/useFile';
import useConfig from '@/store/useConfig';
import dynamic from 'next/dynamic';

const ModalController = dynamic(() => import('@/layout/ModalController'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: false,
    },
  },
});

export const StyledPageWrapper = styled.div`
  height: calc(100vh - 27px);
  width: 100%;

  @media only screen and (max-width: 320px) {
    height: 100vh;
  }
`;

export const StyledEditorWrapper = styled.div`
  width: 100%;
  height: 100%;
  overflow: hidden;
`;

export const HomePage = () => {
  const { query, isReady } = useRouter();
  const { setColorScheme } = useMantineColorScheme();
  const checkEditorSession = useFile((state) => state.checkEditorSession);
  const darkmodeEnabled = useConfig((state) => state.darkmodeEnabled);

  React.useEffect(() => {
    if (isReady) checkEditorSession(query?.json);
  }, [checkEditorSession, isReady, query]);

  React.useEffect(() => {
    setColorScheme(darkmodeEnabled ? 'dark' : 'light');
  }, [darkmodeEnabled, setColorScheme]);

  return (
    <Layout>
      <NextSeo {...SEO} canonical="https://json-to-diagram.devsolux.net" />
      <SoftwareAppJsonLd
        name="JSON2Diagram"
        price="0"
        priceCurrency="EUR"
        type="DeveloperApplication"
        operatingSystem="All"
        keywords="json, json editor, json viewer, json formatter, json beautifier, json validator, json minifier, json compressor, json decompressor, json parser, json converter, json to yaml, json to xml, json to csv, json to tsv, json to html, json to markdown, json to base64, json to url, json to query string, json to form data, json to javascript object, json to php array, json to python dictionary, json to ruby hash, json to java object, json to c# object, json to go object, json to rust object, json to swift object, json to kotlin object, json to typescript object, json to graphql, json to sql, json to mongodb, json to yaml, yaml to json, xml to json, csv to json, tsv to json, html to json, markdown to json, base64 to json, url to json, query string to json, form data to json, javascript object to json, php array to json, python dictionary to json, ruby hash to json, java object to json, c# object to json, go object to json, rust object to json, swift object to json, kotlin object to json, typescript object to json, graphql to json, sql to json, mongodb to json, yaml to json, json to yaml, xml to json, csv to json, tsv to json, html to json, markdown to json, base64 to json, url to json, query string to json, form data to json, javascript object to json, php array to json, python dictionary to json, ruby hash to json, java object to json, c# object to json, go object to json, rust object to json, swift object to json, kotlin object to json, typescript object to json, graphql to json, sql to json, mongodb to json"
        applicationCategory="DeveloperApplication"
      />
      <ThemeProvider theme={darkmodeEnabled ? darkTheme : lightTheme}>
        <QueryClientProvider client={queryClient}>
          <ModalController />
          <StyledEditorWrapper>
            <StyledPageWrapper>
              <Toolbar />
              <StyledEditorWrapper>
                <Editor />
              </StyledEditorWrapper>
            </StyledPageWrapper>
            <BottomBar />
          </StyledEditorWrapper>
        </QueryClientProvider>
      </ThemeProvider>
    </Layout>
  );
};

export default HomePage;
